import React, { useState, useEffect } from 'react';
import '../styles/yourInformation.css';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { useShoppingCart } from "../hooks/useShoppingCart";
import { validateVatNumber } from '../services/apiCalls';

const YourInformation = () => {
    const { subscription, addons, changeformValues, formValues, validatedVat, validatedVatStatus, cacheVatValidation } = useShoppingCart();
    const {t} = useTranslation();
    const navigate = useNavigate();

    const [vatStatus, setVatStatus] = useState(validatedVat === formValues.vatNumber ? validatedVatStatus : null);
    const [vatLoading, setVatLoading] = useState(false);

    const countryOptions = [{Name: 'Belgium', ISO: 'BE'}, {Name: 'France', ISO: 'FR'}, {Name: 'Netherlands', ISO: 'NL'}];
    // const [localFormValues, setFormValues] = useState({
    //     companyName: "",
    //     vatNumber: "",
    //     contactFirstName: "",
    //     contactLastName: "",
    //     billingEmail: "",
    //     phone: "",
    //     street: "",
    //     postalCode: "",
    //     city: "",
    //     country: "",
    //     ggnNumber: "",
    //     comment: "",
    //     allValid: false
    // });

    const handleChange = (e, newVatStatus) => {
        changeformValues(prevValues => {
            let updatedValues = { ...prevValues };

            if (e) {
                const { name } = e.target;
                let { value } = e.target;

                if (name === "vatNumber") {
                    value = value.replaceAll(/[^a-zA-Z0-9]/g, '').toUpperCase();
                }

                updatedValues[name] = value;

                if (name === "country") {
                    const countryObj = countryOptions.find(c => c.Name === value);
                    updatedValues.countryISO = countryObj ? countryObj.ISO : "";
                }
            }

            const isVatValueChanging = e?.target?.name === "vatNumber" && e?.target?.value !== validatedVat;
            const allValid = isVatValueChanging ? false : areRequiredFieldsValid(
                updatedValues,
                newVatStatus ?? vatStatus
            );

            const emailsUnique = emailsAreUnique(updatedValues);

            return {
                ...updatedValues,
                allValid,
                emailsUnique
            };
        });
    };
    
    // Utility function to check if form values are valid
    const areRequiredFieldsValid = (values, vatStatus) => {
        const requiredFields = [
            "companyName",
            "vatNumber",
            "contactFirstName",
            "contactLastName",
            "billingEmail",
            "phone",
            "street",
            "postalCode",
            "city",
            "country"
        ];

        const isValidEmail = emailRegex.test(values['billingEmail']);
        const isValidPhone = phoneRegex.test(values['phone']);

        const allRequiredFilled = requiredFields.every(
            field => values[field]?.trim() !== ""
        );

        // console.log('allRequiredFilled:', allRequiredFilled, 'isValidEmail:', isValidEmail, 'isValidPhone:', isValidPhone, 'vatStatus:', vatStatus, 'vatLoading:', vatLoading);

        return allRequiredFilled && isValidEmail && isValidPhone && vatStatus === "valid" && vatLoading === false;
    };

    const validateVat = async (vat) => {
        if (vat === validatedVat) {
            setVatStatus(validatedVatStatus);
            return;
        }

        try {
            setVatLoading(true);

            const data = await validateVatNumber(vat);

            const status = data.valid ? "valid" : "invalid";
            setVatStatus(status);
            cacheVatValidation(vat, status);

            setVatLoading(false);
            handleChange(null, status);

        } catch (e) {
            // VIES could not verify the number (usually transient) - don't cache, let the user retry
            setVatStatus("unavailable");
            setVatLoading(false);
            handleChange(null, "unavailable");
        }
    };
    
    useEffect(() => {
        if(!subscription){
            navigate("/subscriptions")
        }
    },[navigate, subscription])

    useEffect(() => {
        const vat = formValues.vatNumber;

        if (!vat || vat.trim().length < 4) {
            setVatStatus(null);
            return;
        }

        if (vat === validatedVat) {
            setVatStatus(validatedVatStatus);
            return;
        }

        const timer = setTimeout(() => {
            validateVat(vat);
        }, 600); // milliseconds to wait

        return () => clearTimeout(timer); // cancel if user keeps typing
    }, [formValues.vatNumber, validatedVat, validatedVatStatus]);

    // const handleSubmit = (e) => {
    //     e.preventDefault();
    //     console.log("Form values:", localFormValues);

    //     // send to API or store in context etc.
    // };

    const addonsThatRequireContactInfo = addons.filter(addon => addon.requiresContactInfo)
    const addonsThatRequireCompanyInfo = addons.filter(addon => addon.requiresCompanyInfo)

    return (
        <div className="container mt-4">
            <form>

                <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label">
                            {t('company_name')}<span style={{ color: 'red', marginLeft: '0.25em' }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="companyName"
                            maxLength="255"
                            value={formValues.companyName}
                            onChange={handleChange}
                            className="form-control"
                            required
                        />
                    </div>

                    <div className="col-md-6">
                        <label className="form-label">
                            {t('vat_number')}
                            <span style={{ color: 'red', marginLeft: '0.25em' }}>*</span>
                        </label>

                        <div className="position-relative">
                            <input
                                type="text"
                                name="vatNumber"
                                maxLength="15"
                                value={formValues.vatNumber}
                                onChange={handleChange}
                                className={`form-control pe-5 ${
                                    !vatLoading && vatStatus === "invalid" ? "is-invalid" : ""
                                }`}
                                required
                            />

                            {vatLoading && (
                                <div
                                    className="position-absolute top-50 end-0 translate-middle-y me-3"
                                    style={{ pointerEvents: "none" }}
                                >
                                    <div className="spinner-border spinner-border-sm" role="status" />
                                </div>
                            )}
                        </div>
                        {!vatLoading && vatStatus === "unavailable" && (
                            <VatUnavailableNotice onRetry={() => validateVat(formValues.vatNumber)} />
                        )}
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label">
                            {t('contact_first_name')}<span style={{ color: 'red', marginLeft: '0.25em' }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="contactFirstName"
                            maxLength="40"
                            value={formValues.contactFirstName}
                            onChange={handleChange}
                            className="form-control"
                            required
                        />
                    </div>

                    <div className="col-md-6">
                        <label className="form-label">
                            {t('contact_last_name')}<span style={{ color: 'red', marginLeft: '0.25em' }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="contactLastName"
                            maxLength="80"
                            value={formValues.contactLastName}
                            onChange={handleChange}
                            className="form-control"
                            required
                        />
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label">
                            {t('billing_email')}<span style={{ color: 'red', marginLeft: '0.25em' }}>*</span>
                        </label>
                        <input
                            type="email"
                            name="billingEmail"
                            maxLength="80"
                            value={formValues.billingEmail}
                            onChange={handleChange}
                            className={`form-control ${formValues.billingEmail &&!emailRegex.test(formValues.billingEmail) ? "is-invalid": ""}`}
                            required
                        />
                    </div>

                    <div className="col-md-6">
                        <label className="form-label">
                            {t('phone_number')}<span style={{ color: 'red', marginLeft: '0.25em' }}>*</span>
                        </label>
                        <input
                            type="tel"
                            name="phone"
                            maxLength="40"
                            value={formValues.phone}
                            onChange={handleChange}
                            className={`form-control ${formValues.phone && !phoneRegex.test(formValues.phone) ? "is-invalid" : ""}`}
                            required
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <label className="form-label">
                        {t('street')}<span style={{ color: 'red', marginLeft: '0.25em' }}>*</span>
                    </label>
                    <input
                        type="text"
                        name="street"
                        maxLength="255"
                        value={formValues.street}
                        onChange={handleChange}
                        className="form-control"
                        required
                    />
                </div>

                <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label">
                            {t('postal_code')}<span style={{ color: 'red', marginLeft: '0.25em' }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="postalCode"
                            maxLength="20"
                            value={formValues.postalCode}
                            onChange={handleChange}
                            className="form-control"
                            required
                        />
                    </div>

                    <div className="col-md-6">
                        <label className="form-label">
                            {t('city')}<span style={{ color: 'red', marginLeft: '0.25em' }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="city"
                            maxLength="40"
                            value={formValues.city}
                            onChange={handleChange}
                            className="form-control"
                            required
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <label className="form-label">
                        {t('country')}
                        <span style={{ color: 'red', marginLeft: '0.25em' }}>*</span>
                    </label>
                    <select
                        name="country"
                        value={formValues.country}
                        onChange={handleChange}
                        className="form-control"
                        required
                    >
                        {!formValues.country && <option value={""}>{t('select_country')}</option>}
                        {countryOptions.map(c => (
                            <option key={c.ISO} value={c.Name}>{t(`${c.Name}`)}</option>
                        ))}
                    </select>
                </div>

                <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label">{t('ggn_number')}</label>
                        <input
                            type="text"
                            name="ggnNumber"
                            maxLength="15"
                            value={formValues.ggnNumber}
                            onChange={handleChange}
                            className="form-control"
                        />
                    </div>

                    <div className="col-md-6">
                        <label className="form-label d-inline-flex align-items-center gap-2">
                            {t('activation_code')}
                            <span
                                className="badge rounded-pill bg-light text-dark border"
                                title={t('activation_code_tooltip')}
                                aria-label={t('activation_code_tooltip')}
                            >
                                ?
                            </span>
                        </label>
                        <input
                            type="text"
                            name="activationCode"
                            maxLength="50"
                            value={formValues.activationCode || ""}
                            onChange={handleChange}
                            title={t('activation_code_tooltip')}
                            className="form-control"
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <label className="form-label">{t('comment')}</label>
                    <textarea
                        name="comment"
                        maxLength="32000"
                        value={formValues.comment}
                        onChange={handleChange}
                        className="form-control"
                        rows="3"
                    ></textarea>
                </div>

                {addonsThatRequireContactInfo.length > 0 &&
                    addonsThatRequireContactInfo.map((product) => (
                        <ExtraContactInformation
                            key={product.id}
                            productId={product.id}
                            productName={product.name}
                            quantity={product.quantity}
                        />
                    ))
                }
                
                {addonsThatRequireCompanyInfo.length > 0 &&
                    addonsThatRequireCompanyInfo.map((product) => (
                        <ExtraCompanyInformation
                            key={product.id}
                            productId={product.id}
                            productName={product.name}
                            quantity={product.quantity}
                        />
                    ))
                }

            </form>
        </div>
    );
};

export default YourInformation;

const ExtraContactInformation = ({ productId, productName, quantity }) => {
    const { t } = useTranslation();
    const { formValues, changeformValues, addons } = useShoppingCart();

    // Ensure structure exists
    useEffect(() => {
        if (!formValues.addonContacts || !formValues.addonContacts[productId]) {
            const productEntries = Array.from({ length: quantity }).map(() => ({
                contactFirstName: "",
                contactLastName: "",
                billingEmail: "",
                phone: "",
            }));

            changeformValues({
                ...formValues,
                addonContacts: {
                    ...(formValues.addonContacts || {}),
                    [productId]: productEntries
                }
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validateAll = (updatedValues) => {
        const addons = updatedValues.addonContacts || {};

        const allEmails = [];

        if (updatedValues.billingEmail?.trim()) {
            allEmails.push(updatedValues.billingEmail.trim().toLowerCase());
        }

        const allValid = Object.values(addons).every(productArray =>
            productArray.every(entry =>
                entry.contactFirstName?.trim() &&
                entry.contactLastName?.trim() &&
                entry.billingEmail?.trim() &&
                entry.phone?.trim() &&
                emailRegex.test(entry.billingEmail) &&
                phoneRegex.test(entry.phone)
            )
        );

        const emailsUnique = emailsAreUnique(updatedValues)

        changeformValues({
            ...updatedValues,
            allAddonContactsValid: allValid,
            emailsUnique
        });
    };

    const handleAddonChange = (index, field, value) => {
        const updatedProductEntries = [...formValues.addonContacts[productId]];
        updatedProductEntries[index][field] = value;

        const updatedValues = {
            ...formValues,
            addonContacts: {
                ...formValues.addonContacts,
                [productId]: updatedProductEntries
            }
        };

        validateAll(updatedValues);   // also updates allAddonContactsValid
    };


    return (
        <div>
            {Array.from({ length: quantity }).map((_, index) => {
                const entry = formValues.addonContacts?.[productId]?.[index] || {};

                return (
                    <div key={index} className="mb-4" style={{ borderTop: "1px solid white", paddingTop: "1em" }}>
                        <h5 className="mb-3" style={{ fontWeight: "bold", color: 'white'}}>
                            {productName} {quantity > 1 ? `(${index + 1})` : ""}
                        </h5>

                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">
                                    {t("contact_first_name")}<span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    maxLength="40"
                                    value={entry.contactFirstName ?? ""}
                                    onChange={(e) =>
                                        handleAddonChange(index, "contactFirstName", e.target.value)
                                    }
                                    className="form-control"
                                    required
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">
                                    {t("contact_last_name")}<span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    maxLength="80"
                                    value={entry.contactLastName ?? ""}
                                    onChange={(e) =>
                                        handleAddonChange(index, "contactLastName", e.target.value)
                                    }
                                    className="form-control"
                                    required
                                />
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">
                                    {t("email")}<span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                    type="email"
                                    maxLength="80"
                                    value={entry.billingEmail ?? ""}
                                    onChange={(e) =>
                                        handleAddonChange(index, "billingEmail", e.target.value)
                                    }
                                    className={`form-control ${entry.billingEmail && (!emailRegex.test(entry.billingEmail) || isDuplicateEmail(formValues, entry.billingEmail, productId, index)) ? "is-invalid" : ""}`}
                                    required
                                />
                                {isDuplicateEmail(formValues, entry.billingEmail, productId, index) && (
                                    <div className="invalid-feedback">
                                        {t("email_already_used")}
                                    </div>
                                )}
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">
                                    {t("phone_number")}<span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                    type="tel"
                                    maxLength="40"
                                    value={entry.phone ?? ""}
                                    onChange={(e) =>
                                        handleAddonChange(index, "phone", e.target.value)
                                    }
                                    className={`form-control ${entry.phone && !phoneRegex.test(entry.phone) ? "is-invalid" : ""}`}
                                    required
                                />
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const VatUnavailableNotice = ({ onRetry }) => {
    const { t } = useTranslation();

    return (
        <div className="form-text text-warning">
            {t('vat_service_unavailable')}{" "}
            <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={onRetry}>
                {t('retry')}
            </button>
        </div>
    );
};

const ExtraCompanyInformation = ({ productId, productName, quantity }) => {
    const { t } = useTranslation();
    const { formValues, changeformValues } = useShoppingCart();

    // Track VAT validation state per entry index, e.g. { 0: "valid", 1: "invalid" }
    const [vatStatuses, setVatStatuses] = useState({});
    const [vatLoadingMap, setVatLoadingMap] = useState({});

    const countryOptions = [{Name: 'Belgium', ISO: 'BE'}, {Name: 'France', ISO: 'FR'}, {Name: 'Netherlands', ISO: 'NL'}];

    // Ensure structure exists AND stays in sync with quantity changes
    useEffect(() => {
        const existing = formValues.addonCompanies?.[productId] || [];

        if (existing.length === quantity) return; // already in sync

        let updatedEntries;

        if (existing.length < quantity) {
            // top up with empty entries, keep existing ones intact
            const emptyEntry = () => ({
                companyName: "",
                vatNumber: "",
                street: "",
                postalCode: "",
                city: "",
                country: "",
                countryISO: "",
                ggnNumber: "",
            });
            updatedEntries = [
                ...existing,
                ...Array.from({ length: quantity - existing.length }, emptyEntry)
            ];
        } else {
            // quantity decreased, trim extra entries
            updatedEntries = existing.slice(0, quantity);
        }

        const updatedValues = {
            ...formValues,
            addonCompanies: {
                ...formValues.addonCompanies,
                [productId]: updatedEntries
            }
        };

        validateAll(updatedValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quantity, productId]);

    const validateAll = (updatedValues, overrideVatStatuses) => {
        const companies = updatedValues.addonCompanies || {};
        const effectiveVatStatuses = overrideVatStatuses ?? vatStatuses;

        const allValid = Object.entries(companies).every(([pid, productArray]) =>
            productArray.every((entry, idx) => {
                const requiredFilled = [
                    "companyName", "vatNumber", "street", "postalCode", "city", "country"
                ].every(field => entry[field]?.trim() !== "");

                const key = `${pid}-${idx}`;
                const vatOk = effectiveVatStatuses[key] === "valid";

                return requiredFilled && vatOk;
            })
        );

        changeformValues({
            ...updatedValues,
            allAddonCompaniesValid: allValid,
        });
    };

    const handleAddonChange = (index, field, value) => {
        const updatedProductEntries = [...formValues.addonCompanies[productId]];
        let newValue = value;

        if (field === "vatNumber") {
            newValue = value.replaceAll(/[^a-zA-Z0-9]/g, '').toUpperCase();
        }

        updatedProductEntries[index] = {
            ...updatedProductEntries[index],
            [field]: newValue
        };

        if (field === "country") {
            const countryObj = countryOptions.find(c => c.Name === value);
            updatedProductEntries[index].countryISO = countryObj ? countryObj.ISO : "";
        }

        const updatedValues = {
            ...formValues,
            addonCompanies: {
                ...formValues.addonCompanies,
                [productId]: updatedProductEntries
            }
        };

        // If VAT number changes, invalidate cached status for this entry until revalidated
        if (field === "vatNumber") {
            const key = `${productId}-${index}`;
            setVatStatuses(prev => {
                const next = { ...prev };
                delete next[key];
                validateAll(updatedValues, next);
                return next;
            });
        } else {
            validateAll(updatedValues);
        }
    };

    const validateEntryVat = async (index, vat) => {
        const key = `${productId}-${index}`;

        if (!vat || vat.trim().length < 4) {
            setVatStatuses(prev => {
                const next = { ...prev };
                delete next[key];
                validateAll(formValues, next);
                return next;
            });
            return;
        }

        try {
            setVatLoadingMap(prev => ({ ...prev, [key]: true }));

            const data = await validateVatNumber(vat);
            const status = data.valid ? "valid" : "invalid";

            setVatStatuses(prev => {
                const next = { ...prev, [key]: status };
                validateAll(formValues, next);
                return next;
            });
        } catch (e) {
            setVatStatuses(prev => {
                const next = { ...prev, [key]: "unavailable" };
                validateAll(formValues, next);
                return next;
            });
        } finally {
            setVatLoadingMap(prev => ({ ...prev, [key]: false }));
        }
    };

    // Debounced VAT validation per entry
    useEffect(() => {
        const entries = formValues.addonCompanies?.[productId] || [];
        const timers = entries.map((entry, index) => {
            const key = `${productId}-${index}`;
            const vat = entry.vatNumber;

            if (!vat || vat.trim().length < 4) return null;
            if (vatStatuses[key]) return null; // already validated for current value

            return setTimeout(() => {
                validateEntryVat(index, vat);
            }, 600);
        });

        return () => timers.forEach(timer => timer && clearTimeout(timer));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues.addonCompanies?.[productId]]);

    return (
        <div>
            {Array.from({ length: quantity }).map((_, index) => {
                const entry = formValues.addonCompanies?.[productId]?.[index] || {};
                const key = `${productId}-${index}`;
                const vatStatus = vatStatuses[key];
                const vatLoading = vatLoadingMap[key];

                return (
                    <div key={index} className="mb-4" style={{ borderTop: "1px solid white", paddingTop: "1em" }}>
                        <h5 className="mb-3" style={{ fontWeight: "bold", color: 'white' }}>
                            {productName} {quantity > 1 ? `(${index + 1})` : ""}
                        </h5>

                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">
                                    {t('company_name')}<span style={{ color: 'red', marginLeft: '0.25em' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    maxLength="255"
                                    value={entry.companyName ?? ""}
                                    onChange={(e) => handleAddonChange(index, "companyName", e.target.value)}
                                    className="form-control"
                                    required
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">
                                    {t('vat_number')}<span style={{ color: 'red', marginLeft: '0.25em' }}>*</span>
                                </label>
                                <div className="position-relative">
                                    <input
                                        type="text"
                                        maxLength="15"
                                        value={entry.vatNumber ?? ""}
                                        onChange={(e) => handleAddonChange(index, "vatNumber", e.target.value)}
                                        className={`form-control pe-5 ${
                                            !vatLoading && vatStatus === "invalid" ? "is-invalid" : ""
                                        }`}
                                        required
                                    />
                                    {vatLoading && (
                                        <div
                                            className="position-absolute top-50 end-0 translate-middle-y me-3"
                                            style={{ pointerEvents: "none" }}
                                        >
                                            <div className="spinner-border spinner-border-sm" role="status" />
                                        </div>
                                    )}
                                </div>
                                {!vatLoading && vatStatus === "unavailable" && (
                                    <VatUnavailableNotice onRetry={() => validateEntryVat(index, entry.vatNumber)} />
                                )}
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">
                                {t('street')}<span style={{ color: 'red', marginLeft: '0.25em' }}>*</span>
                            </label>
                            <input
                                type="text"
                                maxLength="255"
                                value={entry.street ?? ""}
                                onChange={(e) => handleAddonChange(index, "street", e.target.value)}
                                className="form-control"
                                required
                            />
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">
                                    {t('postal_code')}<span style={{ color: 'red', marginLeft: '0.25em' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    maxLength="20"
                                    value={entry.postalCode ?? ""}
                                    onChange={(e) => handleAddonChange(index, "postalCode", e.target.value)}
                                    className="form-control"
                                    required
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">
                                    {t('city')}<span style={{ color: 'red', marginLeft: '0.25em' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    maxLength="40"
                                    value={entry.city ?? ""}
                                    onChange={(e) => handleAddonChange(index, "city", e.target.value)}
                                    className="form-control"
                                    required
                                />
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">
                                    {t('country')}<span style={{ color: 'red', marginLeft: '0.25em' }}>*</span>
                                </label>
                                <select
                                    value={entry.country ?? ""}
                                    onChange={(e) => handleAddonChange(index, "country", e.target.value)}
                                    className="form-control"
                                    required
                                >
                                    {!entry.country && <option value={""}>{t('select_country')}</option>}
                                    {countryOptions.map(c => (
                                        <option key={c.ISO} value={c.Name}>{t(`${c.Name}`)}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">{t('ggn_number')}</label>
                                <input
                                    type="text"
                                    maxLength="15"
                                    value={entry.ggnNumber ?? ""}
                                    onChange={(e) => handleAddonChange(index, "ggnNumber", e.target.value)}
                                    className="form-control"
                                />
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const emailRegex = /^[A-Za-z0-9.+-]+@([A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/;
// Very permissive international phone check (+, spaces, dashes allowed)
const phoneRegex = /^\+?[0-9\s\-().]{7,}$/;

const isDuplicateEmail = (updatedValues, emailToCheck, productId, index) => {
    if (!emailToCheck) return false;

    const normalized = emailToCheck.trim().toLowerCase();
    let occurrences = 0;

    // main billing email
    if (
        updatedValues.billingEmail &&
        updatedValues.billingEmail.trim().toLowerCase() === normalized
    ) {
        occurrences++;
    }

    const addons = updatedValues.addonContacts || {};

    Object.entries(addons).forEach(([pid, productArray]) => {
        productArray.forEach((entry, i) => {
            if (
                entry.billingEmail &&
                entry.billingEmail.trim().toLowerCase() === normalized
            ) {
                occurrences++;
            }
        });
    });

    return occurrences > 1;
};

const emailsAreUnique = (updatedValues) => {
    const emails = [];

    // main billing email
    if (updatedValues.billingEmail?.trim()) {
        emails.push(updatedValues.billingEmail.trim().toLowerCase());
    }

    // addon emails
    const addons = updatedValues.addonContacts || {};

    Object.values(addons).forEach(productArray => {
        productArray.forEach(entry => {
            const email = entry.billingEmail?.trim().toLowerCase();
            if (email) {
                emails.push(email);
            }
        });
    });

    return new Set(emails).size === emails.length;
};